<template>
  <AGCard
    :has-rounded-top="!tile"
    :has-rounded-bottom="!tile"
    :class="computedClass"
    :style="computedStyle"
    @click="onClickInside"
  >
    <template v-if="loading">
      <div class="ag-totalizer__header">
        <AGLoader width="60%" class="ag-totalizer__title-skeleton" />
      </div>

      <div class="ag-totalizer__body">
        <div class="ag-totalizer__text">
          <AGLoader width="80px" height="40px" class="ag-totalizer__total-skeleton" />
          <AGLoader v-if="description" width="80%" class="ag-totalizer__description-skeleton" />
        </div>

        <AGLoader v-if="$slots.chart" width="80px" height="80px" radius="40px" class="ag-totalizer__chart-skeleton" />
      </div>
    </template>

    <template v-else>
      <div class="ag-totalizer__header">
        <slot name="title">
          <p class="ag-totalizer__title" :data-qa="normalizeQa(titleDataQa)" v-html="title" />
        </slot>

        <slot name="icon">
          <AGTooltip v-if="icon" :disabled="!iconTooltip" :tooltip-text="iconTooltip">
            <AGIcon color="primary" :icon="icon" />
          </AGTooltip>
        </slot>
      </div>

      <div class="ag-totalizer__body">
        <div class="ag-totalizer__text">
          <p class="ag-totalizer__total" :data-qa="normalizeQa(valueDataQa)" v-html="data" />
          <p
            v-if="description"
            class="ag-totalizer__description"
            :data-qa="normalizeQa(descriptionDataQa)"
            v-html="description"
          />
        </div>

        <div v-if="$slots.chart" class="ag-totalizer__chart">
          <slot name="chart" />
        </div>
      </div>
    </template>
  </AGCard>
</template>

<script>
import AGCard from '@/components/common/AGCard.vue';
import AGIcon from '@/components/common/AGIcon.vue';
import AGLoader from '@/components/common/AGLoader.vue';
import AGTooltip from '@/components/common/AGTooltip.vue';
import UtilsString from '@/utils/UtilsString';

export default {
  components: { AGCard, AGLoader, AGTooltip, AGIcon },

  emits: ['update:modelValue', 'click', 'click:active'],

  props: {
    title: {
      type: String,
      default: '',
    },

    description: {
      type: String,
      default: '',
    },

    data: {
      type: [String, Number],
      required: true,
    },

    icon: {
      type: String,
      default: '',
    },

    iconTooltip: {
      type: String,
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    loading: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    modelValue: {
      type: Boolean,
      default: false,
    },

    hasRoundedTop: {
      type: Boolean,
      default: false,
    },

    gap: {
      type: String,
      default: '16px',
    },

    tile: {
      type: Boolean,
      default: false,
    },
    titleDataQa: {
      type: String,
      default: undefined,
    },
    valueDataQa: {
      type: String,
      default: undefined,
    },
    descriptionDataQa: {
      type: String,
      default: undefined,
    },
  },

  computed: {
    localValue: {
      get() {
        return this.modelValue;
      },

      set(val) {
        this.$emit('update:modelValue', val);
      },
    },

    computedClass() {
      let computedClass = 'ag-totalizer';
      if (this.localValue) computedClass += ' ag-totalizer--active';
      if (this.disabled) computedClass += ' ag-totalizer--disabled';
      if (this.loading) computedClass += ' ag-totalizer--loading';
      if (this.readonly) computedClass += ' ag-totalizer--readonly';
      return computedClass;
    },

    computedStyle() {
      return {
        gap: this.gap,
      };
    },

    blockEvents() {
      return this.disabled || this.loading || this.readonly;
    },
  },

  methods: {
    normalizeQa: UtilsString.normalizeQa,
    emitClick() {
      this.$emit('click');
    },

    emitClickActive() {
      this.$emit('click:active');
    },

    onClickInside() {
      if (this.blockEvents) return;
      this.toggleActiveStatus();
      this.emitClickActive();
    },

    toggleActiveStatus() {
      this.localValue = !this.localValue;
      this.emitClick();
    },
  },
};
</script>

<style lang="scss" scoped>
.ag-totalizer {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;

  .ag-totalizer__header {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
  }

  .ag-totalizer__title,
  .ag-totalizer__total,
  .ag-totalizer__description {
    margin: 0;
  }

  .ag-totalizer__title {
    color: $brand-color-primary-medium;
    font-size: $font-size-sm;
  }

  .ag-totalizer__total,
  .ag-totalizer__description {
    color: $neutral-color-high-medium;
  }

  .ag-totalizer__total {
    font-size: $font-size-xl;
    line-height: $font-size-xl;
  }

  .ag-totalizer__description {
    font-size: $font-size-xs;
    min-height: 48px;
  }

  .ag-totalizer__body {
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
  }

  .ag-totalizer__text {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    gap: 4px;
  }

  ::v-deep(&.ag-card-container) {
    transition: height ease-in-out 150ms;
    background-color: #f7f7fd;
  }

  &:hover:not(.ag-totalizer--loading):not(.ag-totalizer--disabled):not(.ag-totalizer--readonly) {
    cursor: pointer;
    background-color: $brand-color-highlight-light;
  }

  &.ag-totalizer--active:not(.ag-totalizer--loading):not(.ag-totalizer--disabled) {
    background-color: white;
    border-top: solid $border-width-thick $brand-color-highlight-medium;
    padding-top: 13px;
  }

  &.ag-totalizer--disabled {
    background-color: var(--v-lowDark-base);
    cursor: default;
  }

  .ag-totalizer__title-skeleton {
    margin-bottom: 20px;
  }

  .ag-totalizer__chart-skeleton {
    flex-shrink: 0;
  }
}
</style>
